import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';
import TierAlertPanel from '../components/TierAlertPanel';
import SizeCatBadge from '../components/badges/SizeCatBadge';

// TIER 1 Monitor — standalone strategy dashboard.
//
// Cert: GBEMI-TIER1-CCH-20260609-954703A7 · Phase 1 LIVE · 3× SIZED · single-slot
// Exit rule: atr_trail (CLOSE-peak + 1.0×ATR giveback · -5×ATR cat stop · EOD).
//
// Reads /strategies/api/strategies/tier1_apremium/decisions/ every 20s.
// One row per TRADE entry + one row per EXIT — same shape as the SHORTSAFE /
// EMEKA monitors. No cross-strategy dependencies.

const REFRESH_MS = 20000;
const TIER1_BLUE = '#0ea5e9';

const ACTION_COLORS = {
  TRADE:  T.bull,
  EXIT:   TIER1_BLUE,
  BLOCK:  T.bear,
  PAUSED: T.gold,
};

function fmtET(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const time = d.toLocaleTimeString('en-GB', {
      timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return `${date} ${time}`;
  } catch { return iso; }
}

const todayETStr = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

function Pill({ value, color }) {
  if (value == null || value === '') return <span style={{ color: T.textMuted }}>—</span>;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
      textTransform: 'uppercase',
      background: `${color || T.border}22`,
      border: `1px solid ${color || T.border}66`,
      color: color || T.text,
    }}>{String(value)}</span>
  );
}

function Stat({ label, value, color, sub }) {
  return (
    <div style={{ flex: '1 1 140px', padding: '12px 14px',
                  borderRadius: 6, background: 'rgba(255,255,255,0.02)',
                  border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 22, color: color || T.text,
                    fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
}

export default function Tier1Monitor() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [limit, setLimit]     = useState(100);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const timerRef = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let url = `${API_BASE}/strategies/api/strategies/tier1_apremium/decisions/?limit=${limit}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (dateFrom) url += `&date_from=${dateFrom}&date_to=${dateTo || dateFrom}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      if (!j.ok) throw new Error(j.error || 'unknown');
      setRows(j.decisions || []);
      setLastFetched(new Date());
    } catch (e) {
      setError(e.message || String(e));
    } finally { setLoading(false); }
  }, [actionFilter, limit, dateFrom, dateTo]);

  useEffect(() => {
    fetchRows();
    timerRef.current = setInterval(fetchRows, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchRows]);

  const stats = rows.reduce((acc, r) => {
    if      (r.action_label === 'TRADE') acc.trades += 1;
    else if (r.action_label === 'EXIT')  acc.exits  += 1;
    else if (r.action_label === 'BLOCK') acc.blocks += 1;
    if (r.action_label === 'EXIT' && r.pnl_bp != null) {
      acc.pnlBp += Number(r.pnl_bp);
      if (Number(r.pnl_bp) > 0) acc.wins   += 1;
      else if (Number(r.pnl_bp) < 0) acc.losses += 1;
    }
    return acc;
  }, { trades: 0, exits: 0, blocks: 0, pnlBp: 0, wins: 0, losses: 0 });

  const resolved = stats.wins + stats.losses;
  const wr = resolved > 0 ? Math.round((stats.wins / resolved) * 100) : null;

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: T.bg }}>
    <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(10px, 4vw, 28px)', maxWidth: 1700, margin: '0 auto' }}>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
                    paddingBottom: 14, borderBottom: `1px solid ${T.border}`,
                    marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>
          TIER 1 Monitor
        </h1>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          Standalone A_PREMIUM · atr_trail exit · cert GBEMI-TIER1-CCH-20260609-954703A7 · Phase 1 LIVE · 3× SIZED · single-slot
          <br />Deployed 2026-06-10 11:48 WAT · Last updated 2026-06-22 12:35 WAT (decisions_view ET-tag)
        </span>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8,
                      alignItems: 'center', flexWrap: 'wrap',
                      justifyContent: 'flex-end', flex: '1 1 360px' }}>
          <label style={{ fontSize: 11, color: T.textMuted }}>From</label>
          <input type="date" value={dateFrom} max={todayETStr()}
                 onChange={(e) => setDateFrom(e.target.value)}
                 style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4,
                          border: `1px solid ${T.border}`, background: T.bg, color: T.text,
                          fontFamily: 'Roboto Mono, monospace',
                          opacity: dateFrom ? 1 : 0.7 }} />
          <label style={{ fontSize: 11, color: T.textMuted }}>To</label>
          <input type="date" value={dateTo} max={todayETStr()}
                 onChange={(e) => setDateTo(e.target.value)}
                 style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4,
                          border: `1px solid ${T.border}`, background: T.bg, color: T.text,
                          fontFamily: 'Roboto Mono, monospace',
                          opacity: dateTo ? 1 : 0.7 }} />
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                  style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700,
                           letterSpacing: '0.05em', textTransform: 'uppercase',
                           borderRadius: 4, cursor: 'pointer',
                           border: `1px solid ${T.border}`,
                           background: 'transparent', color: T.textMuted }}>Clear</button>
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
                  style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4,
                           border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value="">All actions</option>
            <option value="TRADE">TRADE only</option>
            <option value="EXIT">EXIT only</option>
            <option value="BLOCK">BLOCK only</option>
          </select>
          <select value={limit} onChange={e => setLimit(parseInt(e.target.value, 10))}
                  style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4,
                           border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
            <option value={200}>200 rows</option>
            <option value={500}>500 rows</option>
          </select>
          <button onClick={fetchRows} disabled={loading}
                  style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700,
                           letterSpacing: '0.07em', textTransform: 'uppercase',
                           borderRadius: 4, cursor: loading ? 'wait' : 'pointer',
                           border: 'none',
                           background: 'rgba(14,165,233,0.18)', color: TIER1_BLUE }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 10, marginBottom: 12, borderRadius: 4,
                      background: 'rgba(239,68,68,0.1)',
                      border: `1px solid ${T.bear}`, color: T.bear, fontSize: 12 }}>
          Error: {error}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <Stat label="TRADEs"  value={stats.trades} color={ACTION_COLORS.TRADE} />
        <Stat label="EXITs"   value={stats.exits}  color={ACTION_COLORS.EXIT} />
        <Stat label="BLOCKs"  value={stats.blocks} color={ACTION_COLORS.BLOCK} />
        <Stat label="Net bp"  value={stats.pnlBp.toFixed(1)}
              color={stats.pnlBp >= 0 ? T.bull : T.bear}
              sub={`${resolved} closed · WR ${wr != null ? wr + '%' : '—'}`} />
      </div>

      <TierAlertPanel systemId="TIER1" accent={TIER1_BLUE} />

      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8,
                    fontFamily: 'Roboto Mono, monospace' }}>
        TIER 1 Decisions · {rows.length} rows · newest first
        {lastFetched && ` · updated ${lastFetched.toLocaleTimeString('en-GB', { hour12: false })} ET`}
      </div>

      <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`,
                    borderRadius: 6, background: 'rgba(255,255,255,0.01)' }}>
        <table style={{ width: '100%', minWidth: 1120, borderCollapse: 'collapse',
                        fontSize: 11, fontFamily: 'Roboto Mono, monospace' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', textAlign: 'left',
                         color: T.textMuted, fontSize: 10, letterSpacing: '0.05em',
                         textTransform: 'uppercase' }}>
              <th style={{ padding: '8px 10px' }}>Time (ET)</th>
              <th style={{ padding: '8px 10px' }}>Action</th>
              <th style={{ padding: '8px 10px' }}>Cat</th>
              <th style={{ padding: '8px 10px' }}>Side</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Entry $</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Cat Stop $</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Exit $</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Contracts</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>ATR bp</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Peak bp</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Armed</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>PnL bp</th>
              <th style={{ padding: '8px 10px' }}>Reason</th>
              <th style={{ padding: '8px 10px' }}>Signal ID</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={14} style={{ padding: '20px 14px', color: T.textMuted,
                                          textAlign: 'center', fontStyle: 'italic' }}>
                  No TIER 1 decisions yet. A_PREMIUM signals are rare by design.
                  Auto-refreshing every {REFRESH_MS/1000}s.
                </td>
              </tr>
            )}
            {rows.map(r => (
              <tr key={r.id} style={{ borderTop: `1px solid ${T.border}` }}>
                <td style={{ padding: '6px 10px', color: T.text }}>{fmtET(r.bar_time)}</td>
                <td style={{ padding: '6px 10px' }}>
                  <Pill value={r.action_label} color={ACTION_COLORS[r.action_label]} />
                </td>
                <td style={{ padding: '6px 10px' }}>
                  <SizeCatBadge d={r} system="TIER1" />
                </td>
                <td style={{ padding: '6px 10px',
                             color: r.side === 'long' ? T.bull : r.side === 'short' ? T.bear : T.textMuted }}>
                  {(r.side || '—').toUpperCase()}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>
                  {r.entry_price != null ? r.entry_price.toFixed(2) : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: T.bear, fontWeight: 700 }}>
                  {r.entry_price != null && r.cat_stop_bp != null ? (() => {
                    const offset = r.entry_price * r.cat_stop_bp / 10000;
                    const stopPx = r.side === 'short'
                      ? r.entry_price + offset
                      : r.entry_price - offset;
                    return stopPx.toFixed(2);
                  })() : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>
                  {r.exit_price != null ? r.exit_price.toFixed(2) : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>
                  {r.contracts != null ? r.contracts.toFixed(1) + 'c' : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: T.textMuted }}>
                  {r.atr_bp != null ? r.atr_bp.toFixed(2) : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right',
                             color: r.armed ? '#10b981' : T.textMuted }}>
                  {r.peak_bp != null ? r.peak_bp.toFixed(1) : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                  {r.armed != null ? (
                    <Pill value={r.armed ? 'YES' : 'NO'}
                          color={r.armed ? '#10b981' : T.textMuted} />
                  ) : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right',
                             color: r.pnl_bp == null ? T.textMuted
                                  : r.pnl_bp >= 0 ? T.bull : T.bear,
                             fontWeight: r.pnl_bp != null ? 700 : 400 }}>
                  {r.pnl_bp != null ? Number(r.pnl_bp).toFixed(1) : '—'}
                </td>
                <td style={{ padding: '6px 10px', color: T.textMuted, fontSize: 10,
                             maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap' }}
                    title={r.reason || ''}>
                  {r.reason || '—'}
                </td>
                <td style={{ padding: '6px 10px', color: T.textMuted, fontSize: 9,
                             maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap' }}
                    title={r.signal_id || ''}>
                  {r.signal_id || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 10, fontSize: 10, color: T.textMuted,
                    fontFamily: 'Roboto Mono, monospace' }}>
        Cert <strong>GBEMI-TIER1-CCH-20260609-954703A7</strong> · atr_trail exit ·
        −5×ATR cat stop (pre-arm) · arm at 1.5×ATR close-peak · exit on close
        giving back 1.0×ATR · 16:00 ET EOD backstop. Single-slot · 3× SIZED.
      </div>

    </div>
    </div>
  );
}
